import {
    ButtonGradientPrimary,
    Colors,
    htmlToSlate,
    RichText,
    slateToHtml,
    Text,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import type { ThematicMeeting } from '@/types/thematic-meetings';

import { useUpdateThematicMeetingPreparationNotes } from '@/api/thematic-meetings';
import { EDITOR_TOOLBAR_DENSITY } from '@/constants/RichTextEditor';
import { useAmelioTranslations } from '@/hooks';
import { PREPARATION_NOTES_MAX_LENGTH } from '@/pages/ThematicMeetings/constants';
import { useSaveThematicMeeting } from '@/pages/ThematicMeetings/hooks/useSaveThematicMeeting';
import { usePendingSave } from '@/pages/ThematicMeetings/Plan/components/PendingSavesScope';
import { validateMeetingPreparationNotes } from '@/pages/ThematicMeetings/utils/meetingRichText';
import { showSuccessToast } from '@/plugins/Toast';
import { htmlToPlainText } from '@/utils/htmlSanitizer';

const NOTES_SCROLL_BOX =
    '[&_[contenteditable]]:max-h-[220px] [&_[contenteditable]]:overflow-y-auto';

interface PreparationNotesEditorProps {
    meeting: ThematicMeeting;
    language: string;
    dataTestId: string;
}

/**
 * The preparation notes of a planned meeting, saved on request: the save button shows as soon as the
 * text differs from the saved notes, and a failed save keeps the text and the button. The caller keys
 * it by meeting, so a draft never follows the manager to another meeting.
 */
export const PreparationNotesEditor = ({
    meeting,
    language,
    dataTestId,
}: PreparationNotesEditorProps) => {
    const { t } = useAmelioTranslations();
    const { save } = useSaveThematicMeeting();
    const { mutateAsync: updateNotes, isPending: isSaving } =
        useUpdateThematicMeetingPreparationNotes();

    // `RichText` reads its document once, on mount (see `ObjectiveFormPage`'s note), and its counter
    // counts a given `value` rather than the typing: the notes seed it as `defaultValue`.
    const initialNotesSlateValue = useMemo(
        () => htmlToSlate(meeting.preparationNotes ?? ''),
        [meeting.preparationNotes],
    );
    // The saved notes as the editor writes them back: the editor reports its document on focus
    // too, and that must not read as a change.
    const savedNotes = useMemo(() => slateToHtml(initialNotesSlateValue), [initialNotesSlateValue]);

    const [draft, setDraft] = useState<string | null>(null);
    const [hasJustSaved, setHasJustSaved] = useState(false);
    const [refusal, setRefusal] = useState<string | null>(null);

    const changeNotes = (html: string) => {
        setDraft(html);
        setRefusal(null);
        if (html !== savedNotes) {
            setHasJustSaved(false);
        }
    };

    // Saved on request only: a save on leaving the field went unnoticed when the page was left
    // another way, and raced the meeting's completion, which locks the notes.
    const saveNotes = async (html: string): Promise<boolean> => {
        // The counter holds the text to its bound; the server holds the stored HTML to its own, and
        // formatting reaches that one first. Both are checked here, so the refusal names what to cut
        // instead of arriving from the server as "an error occurred".
        const errorMessage = validateMeetingPreparationNotes(html, t);
        if (errorMessage) {
            setRefusal(errorMessage);
            return false;
        }

        const isSaved = await save(() =>
            updateNotes({
                id: meeting.id,
                preparationNotes: htmlToPlainText(html).trim() ? html : null,
            }),
        );
        if (isSaved) {
            setDraft(null);
            setHasJustSaved(true);
        }
        return isSaved;
    };

    // The completion leaves for the list, taking the editor's own confirmation with it.
    const saveNotesBeforeCompleting = async (html: string) => {
        const isSaved = await saveNotes(html);
        if (isSaved) {
            showSuccessToast({ message: t('thematicMeetings.plan.notesSaved') });
        }
        return isSaved;
    };

    const hasUnsavedChanges = draft !== null && draft !== savedNotes;
    usePendingSave(hasUnsavedChanges ? () => saveNotesBeforeCompleting(draft) : null);

    return (
        <RichText
            toolbarDensity={EDITOR_TOOLBAR_DENSITY}
            defaultValue={initialNotesSlateValue}
            onChange={(value) => changeNotes(slateToHtml(value))}
            surface={'glass'}
            contentMinHeight={'compact'}
            // Long notes scroll inside a 220px box, as the design sets it, instead of stretching the
            // section and leaving the questions beside them over empty space.
            className={NOTES_SCROLL_BOX}
            maxLength={PREPARATION_NOTES_MAX_LENGTH}
            error={!!refusal}
            errorMessage={refusal ?? undefined}
            placeholder={t('thematicMeetings.plan.notesPlaceholder')}
            ariaLabel={t('thematicMeetings.plan.notesLabel')}
            locale={language}
            dataTestId={`${dataTestId}-input`}
            // Always given, so the counter keeps its place in the footer when the button comes and goes.
            footerActions={
                // One slot for both: the confirmation and the button never show together, and an
                // empty status beside the button would widen the footer's gap.
                <div className={'flex items-center'}>
                    {/* Always mounted, so the confirmation is announced when it appears. */}
                    <div role={'status'}>
                        {hasJustSaved && (
                            <Text
                                size={'small'}
                                color={Colors.BASE_500}
                            >
                                {t('thematicMeetings.plan.notesSaved')}
                            </Text>
                        )}
                    </div>

                    {hasUnsavedChanges && (
                        <ButtonGradientPrimary
                            size={'sm'}
                            fitContent
                            loading={isSaving}
                            disabled={isSaving}
                            onClick={() => void saveNotes(draft)}
                            dataTestId={`${dataTestId}-save`}
                        >
                            {t('thematicMeetings.plan.saveNotes')}
                        </ButtonGradientPrimary>
                    )}
                </div>
            }
        />
    );
};
